import { useEffect, useMemo, useState, useCallback, startTransition } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { getTaskTemplate } from 'src/api/admin/taskTemplates';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormComponent from '@nineeng/forms';
import 'src/styles/phone-mockup.css';
import { Button, ConfirmDialog, LanguageSelect } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getFormBySlug, updateForm } from 'src/api/admin/forms';
import { getSettings } from 'src/api/admin/settings';
import { AVAILABLE_LANGUAGES, Form, LanguageCode, Settings } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TranslationsDialog } from './TranslationsDialog';
import { calculateSpec } from 'src/components/form-builder/helpers/spec';
import { translateForm } from '@repo/shared/helpers';
import { useQuery } from '@tanstack/react-query';
import { getFieldSpec, getSectionSpec } from 'src/components/form-builder/helpers/spec';
import { get as getProp } from 'lodash-es';
import { debounce } from 'lodash-es';
import { Field, Section } from '@repo/shared/domain';

export const Route = createFileRoute('/_admin/config/tasks/$slug/translate')({
	component: TranslateTask,
	loader: async ({ params }) => {
		const taskTemplate = await getTaskTemplate(params.slug);
		const form = await getFormBySlug(taskTemplate?.form || '');
		return { taskTemplate, form };
	},
});

const NullNavigation = () => null;

const BATCH_SIZE = 12;

function useProgressiveList<T>(items: T[]): { visibleItems: T[]; isComplete: boolean } {
	const [visibleCount, setVisibleCount] = useState(Math.min(BATCH_SIZE, items.length));

	useEffect(() => {
		if (visibleCount >= items.length) return;

		const id = requestAnimationFrame(() => {
			startTransition(() => {
				setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, items.length));
			});
		});
		return () => cancelAnimationFrame(id);
	}, [visibleCount, items.length]);

	useEffect(() => {
		setVisibleCount((prev) => {
			if (items.length <= BATCH_SIZE) return items.length;
			// Only reset when the list length changes; avoid resetting on array reference churn.
			return prev > items.length ? items.length : prev;
		});
	}, [items.length]);

	return {
		visibleItems: items.slice(0, visibleCount),
		isComplete: visibleCount >= items.length,
	};
}

const DebouncedForm = ({
	formSchema,
	originalValues,
	onChange,
}: {
	formSchema: Form['formSchema'];
	originalValues: Record<string, unknown>;
	onChange: (update: Record<string, unknown>) => void;
}) => {
	const [localValues, setLocalValues] = useState(originalValues);

	const debouncedOnChange = useMemo(() => debounce(onChange, 300), [onChange]);

	useEffect(() => {
		return () => debouncedOnChange.cancel();
	}, [debouncedOnChange]);

	const onChangeHandler = (update: Record<string, unknown>) => {
		setLocalValues(update);
		debouncedOnChange(update);
	};

	return (
		<FormComponent
			schema={formSchema}
			originalValues={localValues}
			CustomNavigation={NullNavigation}
			onChange={onChangeHandler}
		/>
	);
};

type TranslationForm = {
	id: string;
	title: string;
	formSchema: Form['formSchema'];
	canBeTranslated: boolean;
	originalValues: Record<string, unknown>;
	onChange: (update: Record<string, unknown>) => void;
};

const ProgressiveFormList = ({
	forms,
	selectedLanguage,
}: {
	forms: TranslationForm[];
	selectedLanguage: LanguageCode;
}) => {
	const { visibleItems, isComplete } = useProgressiveList(forms);

	return (
		<>
			<Alert severity="info">
				Videos, images & some other fields are not automatically translated. Please translate them manually.
			</Alert>
			{visibleItems.map((form) => (
				<div
					key={form.id + '-' + selectedLanguage}
					style={{
						border: '1px solid #e0e0e0',
						borderRadius: 8,
						padding: 16,
					}}
				>
					<Typography variant="subtitle2" style={{ marginBottom: 8 }}>
						{form.title}
					</Typography>
					{!form.canBeTranslated && (
						<Alert severity="info">
							This section already has been translated by the NineID team. You can&apos;t modify these translations.
						</Alert>
					)}
					{form.canBeTranslated && (
						<DebouncedForm formSchema={form.formSchema} originalValues={form.originalValues} onChange={form.onChange} />
					)}
				</div>
			))}
			{!isComplete && (
				<div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
					<CircularProgress size={24} />
				</div>
			)}
		</>
	);
};

function TranslateTask() {
	const { taskTemplate, form } = Route.useLoaderData();
	const { slug } = Route.useParams();
	const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
	const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(() => {
		const originalLanguage = (form?.language as LanguageCode) || 'en';
		const firstTranslated = form?.availableLanguages?.find((lng: LanguageCode) => lng !== originalLanguage);
		return (firstTranslated as LanguageCode) || originalLanguage;
	});
	const spec = useMemo(() => calculateSpec(), []);
	const [translations, setTranslations] = useState<Form['translations'] | undefined>(form?.translations);
	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [isTranslationsDialogOpen, setTranslationsDialogOpen] = useState(false);
	const [isUnsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

	const { data: liveForm } = useQuery({
		queryKey: ['form-translation-status', form?.slug],
		queryFn: () => getFormBySlug(form?.slug || ''),
		enabled: !!form?.slug,
		refetchInterval: (query) => {
			return query.state.data?.translationStatus === 'translating' ? 3000 : false;
		},
	});
	const isTranslating = (liveForm ?? form)?.translationStatus === 'translating';

	useEffect(() => {
		if (liveForm && !isTranslating && liveForm.translations) {
			setTranslations(liveForm.translations);
			const newLanguages = (liveForm.availableLanguages || []).filter(
				(lng: LanguageCode) => !form?.availableLanguages?.includes(lng),
			);
			if (newLanguages.length > 0) {
				setSelectedLanguage(newLanguages[0] as LanguageCode);
			}
		}
	}, [liveForm, isTranslating, form?.availableLanguages]);

	const { data: languagesSettings = {} } = useQuery({
		queryKey: ['settings', Settings.Languages],
		queryFn: () => getSettings(Settings.Languages),
	});

	const translatedForm = useMemo(() => {
		const currentForm = liveForm ?? form;
		if (!currentForm?.id) return currentForm;
		return translateForm({ ...currentForm, id: currentForm.id, translations: translations || {} }, [selectedLanguage]);
	}, [form, liveForm, selectedLanguage, translations]);

	useBreadcrumbLevel(0, 'Configuration', '/config/tasks');
	useBreadcrumbLevel(1, 'Tasks', '/config/tasks');
	useBreadcrumbLevel(2, taskTemplate?.name || 'Task Detail', `/config/tasks/${slug}`);
	useBreadcrumbLevel(3, 'Translate');

	const updateSelectedLanguage = (language: LanguageCode) => {
		setSelectedLanguage(language);
	};

	const currentSection = form?.formSchema?.sections?.[currentSectionIndex];

	const previewForm = useMemo(() => {
		if (!translatedForm?.formSchema?.sections) return translatedForm;
		return {
			...translatedForm,
			formSchema: {
				...translatedForm.formSchema,
				sections: translatedForm.formSchema.sections.map((section: Section) =>
					section
						? {
								...section,
								style: { left: 16, right: 16, top: 32, position: 'inherit', padding: 16 },
							}
						: section,
				),
			},
		};
	}, [translatedForm]);

	const availableLanguages = useMemo(() => {
		const currentForm = liveForm ?? form;
		return [...new Set([currentForm?.language || 'en', ...(currentForm?.availableLanguages ?? [])])];
	}, [form, liveForm]);

	const isOriginalLanguage = selectedLanguage === (form?.language as LanguageCode);

	const saveForm = async () => {
		if (!isDirty) return;
		try {
			// if liveForm is not undefined, we need to update the liveForm with the translations
			// otherwise we have stale data
			await updateForm(form?.slug || '', { ...(liveForm ?? form), translations: translations });
			setIsDirty(false);
			EventEmitter.emit('toast', { type: 'success', message: 'Translations saved' });
		} catch (error) {
			console.error(error);
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to save translations' });
		}
	};

	const updateSectionTranslation = useCallback(
		(sectionId: string, update: Record<string, unknown>) => {
			setTranslations((prev) => {
				const sections = prev?.[selectedLanguage]?.sections || [];
				const section = sections.find((s) => s.id === sectionId);
				if (!section) sections.push({ id: sectionId, ...update });
				else Object.assign(section, update);
				return {
					...prev,
					[selectedLanguage]: {
						...prev?.[selectedLanguage],
						sections,
					},
				};
			});
			setIsDirty(true);
		},
		[selectedLanguage],
	);

	const updateFieldTranslation = useCallback(
		(sectionId: string, fieldId: string, update: Record<string, unknown>) => {
			setTranslations((prev) => {
				const sections = prev?.[selectedLanguage]?.sections || [];
				let section = sections.find((s) => s.id === sectionId);
				if (!section) {
					section = { id: sectionId, fields: [] };
					sections.push(section);
				}
				const field = section?.fields?.find((f) => f.id === fieldId);
				if (!field) section.fields?.push({ id: fieldId, ...update });
				else Object.assign(field, update);
				return {
					...prev,
					[selectedLanguage]: {
						...prev?.[selectedLanguage],
						sections,
					},
				};
			});
			setIsDirty(true);
		},
		[selectedLanguage],
	);
	const forms: TranslationForm[] = useMemo(() => {
		const currentTranslatedSection = translations?.[selectedLanguage]?.sections?.find(
			(s) => s.id === currentSection?.id,
		);
		// if (!currentTranslatedSection) {
		// 	return [];
		// }
		const sectionSpec = getSectionSpec(currentSection, spec);
		const canBeTranslated = (sectionSpec?.translatable?.length ?? 0) > 0;
		const sectionSpecFields = sectionSpec?.fields?.filter((field: Field) =>
			sectionSpec?.translatable?.includes(field.name),
		);
		const sectionFormSchema = {
			type: 'SettingsSection',
			fields: [
				...(sectionSpecFields?.map((field: Field) => {
					const originalValue = field.name ? getProp(currentSection, field.name) : undefined;
					return {
						...field,
						rowLabel: field.label,
						helperText: originalValue && typeof originalValue === 'string' ? 'original: ' + originalValue : undefined,
					};
				}) || []),
			],
		};

		const form = {
			id: currentSection?.id,
			title: 'Section: ' + (sectionSpec?.title || sectionSpec?.name || 'Unknown'),
			formSchema: sectionFormSchema,
			canBeTranslated,
			originalValues: { ...(currentTranslatedSection ?? {}) },
			onChange: (update: Record<string, unknown>) => updateSectionTranslation(currentSection?.id || '', update),
		};

		const fields = currentSection?.fields || [];
		const fieldForms = fields.map((field) => {
			const { id, ...props } = field;

			const translatedProps = currentTranslatedSection?.fields?.find((f) => f.id === id);
			const fieldSpec = getFieldSpec(field, spec);
			const fieldSpecFields =
				fieldSpec?.fields?.filter((fieldFromSpec: Field) => fieldSpec?.translatable?.includes(fieldFromSpec.name)) ??
				[];

			const canBeTranslated = (fieldSpec?.translatable?.length ?? 0) > 0;
			return {
				originalValues: { ...(translatedProps ?? {}) },
				id,
				canBeTranslated,
				title: fieldSpec?.title || fieldSpec?.name || field.key || 'Unknown',
				onChange: (update: Record<string, unknown>) =>
					updateFieldTranslation(currentSection?.id || '', id || '', update),
				formSchema: {
					type: 'SettingsSection',
					fields: fieldSpecFields.map((fieldFromSpec: Field) => {
						const originalValue = fieldFromSpec.name ? getProp(props, fieldFromSpec.name) : undefined;

						return {
							...fieldFromSpec,
							rowLabel: fieldFromSpec.label,
							helperText: originalValue && typeof originalValue === 'string' ? 'original: ' + originalValue : undefined,
						};
					}),
				},
			};
		});

		return [form, ...fieldForms];
	}, [currentSection, spec, translations, selectedLanguage, updateSectionTranslation, updateFieldTranslation]);

	const selectedLanguageName = useMemo(() => {
		return AVAILABLE_LANGUAGES.find((l) => l.code === selectedLanguage)?.name;
	}, [selectedLanguage]);

	const formAvailableLanguages = useMemo(() => {
		return Array.from(
			new Set([form?.language, liveForm?.language, ...((liveForm ?? form)?.availableLanguages || [])]),
		).filter(Boolean) as LanguageCode[];
	}, [form, liveForm]);

	return (
		<>
			<div
				style={{
					width: '100%',
					padding: 32,
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					gap: 32,
					paddingBottom: 96,
					position: 'relative',
				}}
				className="nine-task-overrides"
			>
				<div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
					<h1>{taskTemplate?.name}</h1>
				</div>
				{form?.formSchema && (
					<div
						style={{
							position: 'sticky',
							top: 32,
							alignSelf: 'flex-start',
							maxHeight: 'calc(100vh - 200px)',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<div className="phone-mockup" style={{ flex: '1 1 auto', minHeight: 0 }}>
							<div className="phone-mockup__notch" />
							<div className="phone-mockup__screen">
								<div style={{ overflowY: 'scroll', marginBottom: 32 }}>
									<FormComponent
										schema={previewForm?.formSchema}
										selectedLanguage={selectedLanguage}
										currentSectionIndex={currentSectionIndex}
										onNext={() => setCurrentSectionIndex((i) => i + 1)}
										onPrevious={() => setCurrentSectionIndex((i) => i - 1)}
										CustomNavigation={NullNavigation}
									/>
								</div>
							</div>
						</div>
						<div className="phone-mockup-too-small">
							Your screen is too small to show the phone preview. Please use a larger screen or resize your window.
						</div>
						<div
							style={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: 16,
								marginTop: 32,
								flexShrink: 0,
							}}
						>
							<Alert severity="info">
								Some sections have their own internal navigation! <br />
							</Alert>
							<div
								style={{
									width: '100%',
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
								}}
							>
								<Button
									disabled={currentSectionIndex <= 0}
									onClick={() => setCurrentSectionIndex(currentSectionIndex - 1)}
								>
									<ArrowBackIcon fontSize="small" style={{ marginRight: 4 }} />{' '}
								</Button>
								<Button
									disabled={currentSectionIndex >= (form?.formSchema?.sections?.length ?? 1) - 1}
									onClick={() => setCurrentSectionIndex(currentSectionIndex + 1)}
								>
									<ArrowForwardIcon fontSize="small" style={{ marginLeft: 4 }} />
								</Button>
							</div>
						</div>
					</div>
				)}
				<div style={{ width: 'calc(100% - 480px)', overflowY: 'auto', zIndex: 10, backgroundColor: 'white' }}>
					<div style={{ flexGrow: 1, overflowY: 'auto' }} className="translate-preview">
						<div style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'space-between' }}>
							<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
								{!isOriginalLanguage && <Typography variant="h6">Translate to {selectedLanguageName}</Typography>}
								{isOriginalLanguage && <Typography variant="h6">Start translating!</Typography>}
								<LanguageSelect
									availableLanguages={availableLanguages as LanguageCode[]}
									language={selectedLanguage}
									onLanguageChange={updateSelectedLanguage}
									onTranslationsClick={
										isTranslating
											? undefined
											: () => {
													if (isDirty) {
														setUnsavedChangesDialogOpen(true);
													} else {
														setTranslationsDialogOpen(true);
													}
												}
									}
									customTailwindClasses={{
										selectTrigger: { border: 'border-border', width: 'w-[180px]' },
										selectContent: { width: 'w-[180px]' },
									}}
								/>
							</div>
							{isTranslating ? (
								<Alert severity="warning" icon={<CircularProgress size={20} />}>
									AI translation is currently in progress. Manual editing will be available once it completes.
								</Alert>
							) : isOriginalLanguage ? (
								<Alert severity="info">
									This is the original language of the form. To edit the original content, use the form builder.
									<br />
									To add or manage translations in other languages, use AI-powered translation. <br />
									<Button
										size="sm"
										style={{ marginTop: 8 }}
										onClick={() => {
											if (isDirty) {
												setUnsavedChangesDialogOpen(true);
											} else {
												setTranslationsDialogOpen(true);
											}
										}}
									>
										Manage Translations
									</Button>
								</Alert>
							) : (
								<ProgressiveFormList forms={forms} selectedLanguage={selectedLanguage} />
							)}
						</div>
					</div>
				</div>

				<ConfirmDialog
					isOpen={isUnsavedChangesDialogOpen}
					cancelAction={() => setUnsavedChangesDialogOpen(false)}
					confirmAction={() => {
						setUnsavedChangesDialogOpen(false);
						setTranslationsDialogOpen(true);
					}}
					title="Unsaved Changes"
				>
					You have unsaved translation changes. AI translation will overwrite them. Save your changes first or continue
					to discard them.
				</ConfirmDialog>
				{isTranslationsDialogOpen && (
					<TranslationsDialog
						isOpen={isTranslationsDialogOpen}
						onClose={() => setTranslationsDialogOpen(false)}
						formId={form?.slug}
						formAvailableLanguages={formAvailableLanguages}
						languagesSettings={languagesSettings}
					/>
				)}
			</div>
			{!isOriginalLanguage && !isTranslating && (
				<div
					style={{
						flexShrink: 0,
						width: '100%',
						display: 'flex',
						justifyContent: 'flex-end',
						position: 'absolute',
						zIndex: 20,
						padding: 16,
						bottom: 0,
						right: 0,
						paddingRight: '128px',
						backgroundColor: 'white',
						borderTop: '1px solid #e0e0e0',
					}}
				>
					<Button disabled={!isDirty} onClick={saveForm}>
						Save
					</Button>
				</div>
			)}
		</>
	);
}
